/**
 * Module visibility definitions.
 * Used by Layout.js for nav filtering and UsersTab.js for module assignment.
 * 
 * Each module maps to a nav section key in Layout.js.
 * Role defaults define what each role sees out of the box.
 * Per-user overrides can add or remove modules from their defaults.
 *
 * IMPORTANT: paths here must match the `href` values in Layout.js sidebar items.
 */

// All available modules with their sub-items
//
// v1.1.62 — Cards are ordered + grouped to mirror the LEFT SIDEBAR exactly
// (Assess → Plan → Execute → Optimize → Portfolio → System). This is a UI
// reorganisation only — functionality is unchanged:
//   * Module backing keys ('assessments', 'work_management', 'planning',
//     'portfolio', 'admin') stay identical so user.visible_modules records,
//     ROLE_DEFAULTS, getVisibleModules(), Layout.js's hasModule() and every
//     feature gate continue to work unmodified.
//   * The legacy 'assessments' module is now SPLIT into TWO display cards —
//     'assess' and 'plan' — both backed by the same `assessments` key
//     (`backingKey` field). Toggling either parent toggles the shared
//     module, so checking Assess implicitly checks Plan and vice-versa.
//     This matches the sidebar's visual split without breaking any user
//     records or role defaults.
//   * Sub-items within each card are ordered to match the sidebar's
//     top-to-bottom presentation of the same section.
//
// Side-menu mapping (Layout.js):
//   assess          → "Assess"    section  (backingKey: assessments)
//   plan            → "Plan"      section  (backingKey: assessments)
//   work_management → "Execute"   section
//   planning        → "Optimize"  section
//   portfolio       → "Portfolio" section
//   admin           → "System"    section
export const MODULE_DEFINITIONS = [
  // ── ASSESS ─────────────────────────────────────────────────────────
  {
    key: 'assess', backingKey: 'assessments',
    label: 'Assess', description: 'Assets, buildings, damage, drawings',
    minRole: 3, featureGate: 'fca_enabled',
    items: [
      { path: '/assessments',          label: 'Assets' },
      { path: '/building-inspections', label: 'Buildings', featureGate: 'building_inspections_enabled' },
      { path: '/triage',               label: 'Damage', featureGate: 'damage_triage_enabled' },
      { path: '/spatial/dashboard',    label: 'Drawings', featureGate: 'drawing_intel_enabled' },
    ],
  },
  // ── PLAN ───────────────────────────────────────────────────────────
  {
    key: 'plan', backingKey: 'assessments',
    label: 'Plan', description: 'Capital projects, procurement, planning map',
    minRole: 3, featureGate: 'fca_enabled',
    items: [
      { path: '/capital-projects',     label: 'Capital Projects', featureGate: 'project_management_enabled' },
      { path: '/procurement-planner',  label: 'Procurement', featureGate: 'procurement_planner_enabled' },
      { path: '/planning-map',         label: 'Planning Map' },
    ],
  },
  // ── EXECUTE ────────────────────────────────────────────────────────
  {
    key: 'work_management', label: 'Execute',
    description: 'Work management, my schedule, messaging, operations map',
    minRole: 1, featureGate: 'iwms_enabled',
    items: [
      { path: '/iwms-admin',     label: 'Work Management', minRole: 6 },
      { path: '/work-orders',    label: 'Work Orders' },
      { path: '/pm-schedules',   label: 'PM Schedules', minRole: 3 },
      { path: '/scheduler',      label: 'Scheduler', minRole: 5 },
      { path: '/technician',     label: 'My Schedule', minRole: 2 },
      { path: '/messaging',      label: 'Messaging', minRole: 2 },
      { path: '/operations-map', label: 'Operations Map', minRole: 5 },
    ],
  },
  // ── OPTIMIZE ───────────────────────────────────────────────────────
  {
    key: 'planning', label: 'Optimize',
    description: 'Dashboard, Ask FMiQ-One, Finance & Reporting',
    minRole: 6, featureGate: 'fca_enabled',
    items: [
      { path: '/dashboard',                   label: 'Dashboard' },
      { path: '/ai-dashboard',                label: 'Ask FMiQ-One' },
      { path: '/finance-hub',                 label: 'Finance & Reporting' },
      { path: '/finance-hub?tab=executive',   label: '— Executive Summary' },
      { path: '/finance-hub?tab=maintenance', label: '— Maintenance Costs' },
      { path: '/finance-hub?tab=scorecard',   label: '— Scorecard' },
    ],
  },
  // ── PORTFOLIO ──────────────────────────────────────────────────────
  {
    key: 'portfolio', label: 'Portfolio',
    description: 'Clients, facilities, zones, assets, warranties, leases, vendors',
    minRole: 3,
    items: [
      { path: '/clients',     label: 'Clients', minRole: 5 },
      { path: '/facilities',  label: 'Facilities' },
      { path: '/zones',       label: 'Zones' },
      { path: '/iwms-assets', label: 'Asset Inventory', featureGate: 'iwms_enabled' },
      { path: '/warranties',  label: 'Warranties', featureGate: 'iwms_enabled', minRole: 5 },
      { path: '/leases',      label: 'Leases', featureGate: 'iwms_enabled', minRole: 5 },
      { path: '/vendors',     label: 'Vendors', featureGate: 'iwms_enabled', minRole: 5 },
    ],
  },
  // ── SYSTEM ─────────────────────────────────────────────────────────
  {
    key: 'admin', label: 'System',
    description: 'System admin, brain research, audit log',
    minRole: 6,
    items: [
      { path: '/organization',      label: 'System Admin' },
      { path: '/pricing-ingestion', label: 'Brain Research', minRole: 7 },
      { path: '/audit-log',         label: 'Audit Log' },
      // v1.3.31.8 — API Keys moved into a tab inside System Admin
      // (Super System Admin section). Sidebar/nav link removed; the
      // route /admin/api-keys still resolves for backwards compat.
    ],
  },
];

// Role hierarchy levels (must match backend)
// v1.3.35.30 — Vendor sits at level 0 (isolated) so the existing
// has_minimum_role hierarchy blocks vendors from every non-/vendor
// route by construction.
const ROLE_LEVELS = {
  vendor: 0,
  requester: 1, technician: 2, field_surveyor: 3, inspector: 3, surveyor: 3,
  sme: 4, planner: 5, facility_manager: 5, admin: 6, client_admin: 6,
  super_admin: 7, founder: 8,
};

// Default modules per role (what each role sees out of the box)
export const ROLE_DEFAULTS = {
  // v1.3.35.30 — Vendors don't use the standard module system. Their
  // entire UI is /vendor/* (VendorAssessmentsPage + VendorCapturePage)
  // which lives outside MODULE_DEFINITIONS. Empty array intentional.
  vendor:           [],
  requester:        ['work_management'],
  technician:       ['work_management'],
  field_surveyor:   ['portfolio', 'assessments'],
  inspector:        ['portfolio', 'assessments'],
  surveyor:         ['portfolio', 'assessments'],
  sme:              ['portfolio', 'assessments'],
  planner:          ['portfolio', 'assessments', 'work_management'],
  facility_manager: ['portfolio', 'work_management'],
  admin:            ['admin', 'portfolio', 'planning', 'assessments', 'work_management'],
  client_admin:     ['portfolio', 'planning', 'assessments', 'work_management'],
  super_admin:      ['admin', 'portfolio', 'planning', 'assessments', 'work_management'],
  founder:          ['admin', 'portfolio', 'planning', 'assessments', 'work_management'],
};

// Default hidden items per role (items hidden by default but can be enabled by admin)
export const ROLE_DEFAULT_HIDDEN_ITEMS = {
  vendor:           [],  // v1.3.35.30 — no standard items apply
  requester:        [],
  technician:       [],
  field_surveyor:   [],
  inspector:        [],
  surveyor:         [],
  sme:              [],
  planner:          [],
  facility_manager: [],
  admin:            ['/iwms-admin'],
  client_admin:     [],
  super_admin:      [],
  founder:          [],
};

// Client access rules by role
export const CLIENT_ACCESS_RULES = {
  founder:          'all',        // Sees all clients
  super_admin:      'all',        // Sees all clients
  admin:            'assigned',   // Own assigned client(s) only
  client_admin:     'assigned',   // Own assigned client(s) only
  planner:          'assigned',   // Own assigned client(s) only
  facility_manager: 'assigned',   // Own assigned client(s) only
  sme:              'assigned',   // Own assigned client(s) only
  field_surveyor:   'assigned',   // Own assigned client(s) only
  inspector:        'assigned',   // Own assigned client(s) only
  surveyor:         'assigned',   // Own assigned client(s) only
  technician:       'assigned',   // Own assigned client(s) only
  requester:        'assigned',   // Own assigned client(s) only
  // v1.3.35.30 — Vendor client_id is inherited from their granted
  // assessments. Marked 'assigned' so the standard admin flows still
  // treat them as client-scoped; per-assessment access is enforced
  // via db.assessment_vendors on top.
  vendor:           'assigned',
};

/**
 * Get the effective visible modules for a user.
 * Priority: user override > role defaults
 * @param {object} user - User object with role and optional visible_modules
 * @param {object} features - Feature flags (fca_enabled, iwms_enabled, etc.)
 * @returns {string[]} Array of visible module keys
 */
export function getVisibleModules(user, features = {}) {
  if (!user?.role) {
    // Return unique backing keys (handles cards that share a backingKey,
    // e.g. Assess + Plan both back 'assessments').
    return [...new Set(MODULE_DEFINITIONS.map(m => m.backingKey || m.key))];
  }
  
  const roleLevel = ROLE_LEVELS[user.role] || 1;
  
  let modules;
  if (user.visible_modules && user.visible_modules.length > 0) {
    modules = [...user.visible_modules];
  } else {
    modules = [...(ROLE_DEFAULTS[user.role] || ROLE_DEFAULTS.requester)];
  }
  
  return modules.filter(modKey => {
    // Honor backingKey so stored module keys like 'assessments' still
    // resolve to the Assess + Plan display cards (v1.1.62).
    const modDef = MODULE_DEFINITIONS.find(m => (m.backingKey || m.key) === modKey);
    if (!modDef) return false;
    if (roleLevel < modDef.minRole) return false;
    if (modDef.featureGate && features[modDef.featureGate] === false) return false;
    return true;
  });
}

/**
 * Get the effective hidden items for a user.
 * Priority: user override > role defaults
 */
export function getEffectiveHiddenItems(user) {
  if (!user?.role) return [];
  // If the user has explicit customization (visible_modules set OR a
  // non-empty hidden_items), the saved hidden_items array is the literal
  // truth — even an empty array means "deliberately show everything".
  // Only fall back to role defaults when both fields are pristine.
  const hasCustomModules = Array.isArray(user.visible_modules) && user.visible_modules.length > 0;
  if (hasCustomModules) return Array.isArray(user.hidden_items) ? user.hidden_items : [];
  if (user.hidden_items && user.hidden_items.length > 0) return user.hidden_items;
  return ROLE_DEFAULT_HIDDEN_ITEMS[user.role] || [];
}

/**
 * Check if a specific nav item (by path) is visible for the user.
 */
export function isItemVisible(user, itemPath) {
  if (!user) return true;
  const hiddenItems = getEffectiveHiddenItems(user);
  if (hiddenItems.includes(itemPath)) return false;
  return true;
}

/**
 * Get modules available for a role (for the admin toggle UI).
 */
export function getAvailableModulesForRole(role, features = {}) {
  const roleLevel = ROLE_LEVELS[role] || 1;
  return MODULE_DEFINITIONS.filter(m => {
    if (roleLevel < m.minRole) return false;
    if (m.featureGate && features[m.featureGate] === false) return false;
    return true;
  });
}

/**
 * Check if a role requires client assignment (Admin and below).
 */
export function requiresClientAssignment(role) {
  return CLIENT_ACCESS_RULES[role] === 'assigned';
}

/**
 * Check if a role has access to all clients.
 */
export function hasAllClientAccess(role) {
  return CLIENT_ACCESS_RULES[role] === 'all';
}
